import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DataTable, { TableColumn } from 'react-data-table-component';
import type { CollectionNote } from '../../services/schemas/loanServiceSchemas/collectionNote';
import type { CollectionNotePage } from '../../services/schemas/loanServiceSchemas/collectionNotePage';
import { getCollectionNotes, addCollectionNote } from '../../services/collectionNoteService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { toLocalDateString } from '../../utils/localDate';
import {
  CONTACT_METHOD_LABELS,
  CONTACT_OUTCOME_LABELS,
  CONTACT_OUTCOME_COLORS,
  PROMISE_STATUS_COLORS,
  contactMethodLabel,
  contactOutcomeLabel,
  promiseStatusLabel,
} from '../../constants/collectionFollowUp';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

interface CollectionNotesProps {
  accountId: string;
  className?: string;
  /** Called after a note is saved, so the page can refresh anything that depends on promises. */
  onNoteAdded?: () => void;
}

const CONTEXT_OPTIONS: { value: string; label: string }[] = [
  { value: 'GENERAL', label: 'General' },
  { value: 'ARRANGEMENTS', label: 'Arrangements' },
  { value: 'COLLECTION', label: 'Collection' },
  { value: 'APPLICATION_NOTES', label: 'Application Notes' },
];

const getContextBadgeColor = (context: string): string => {
  const colors: Record<string, string> = {
    GENERAL: 'secondary',
    ARRANGEMENTS: 'info',
    COLLECTION: 'warning',
    APPLICATION_NOTES: 'primary',
  };
  return colors[context] || 'secondary';
};

/** The note columns, shared with the customer's Comments tab (CustomerCollectionNotes). */
export const collectionNoteColumns: TableColumn<CollectionNote>[] = [
  {
    name: 'Date',
    selector: (row) => row.createdDate || '',
    cell: (row) => (
      <span className="text-muted small">{formatDate(row.createdDate || '')}</span>
    ),
    sortable: true,
    width: '130px',
  },
  {
    name: 'Created By',
    selector: (row) => row.createdBy || '',
    cell: (row) => (
      <div className="d-flex align-items-center">
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2 flex-shrink-0"
          style={{ width: '28px', height: '28px', fontSize: '12px' }}
        >
          <i className="fa fa-user"></i>
        </div>
        <span className="fw-semibold">{row.createdBy}</span>
      </div>
    ),
    sortable: true,
    width: '180px',
  },
  {
    name: 'Contact',
    selector: (row) => row.contactMethod || '',
    cell: (row) => row.contactMethod || row.contactPerson ? (
      <div className="py-2">
        <div className="fw-semibold small">{contactMethodLabel(row.contactMethod)}</div>
        {row.contactPerson && <div className="text-muted small">{row.contactPerson}</div>}
      </div>
    ) : (
      <span className="text-muted small">-</span>
    ),
    width: '150px',
  },
  {
    name: 'Outcome',
    selector: (row) => row.outcome || '',
    cell: (row) => {
      if (!row.outcome) return <span className="text-muted small">-</span>;
      return (
        <div className="py-2">
          <span className={`badge bg-${CONTACT_OUTCOME_COLORS[row.outcome] || 'secondary'}`}>
            {contactOutcomeLabel(row.outcome)}
          </span>
          {row.promiseAmount != null && (
            <div className="small mt-1">
              <span className="fw-semibold">{formatCurrency(row.promiseAmount)}</span>
              <span className="text-muted"> by {formatDate(row.promiseDate)}</span>
              <div className="d-flex align-items-center gap-1 mt-1">
                <span className={`badge bg-${PROMISE_STATUS_COLORS[row.promiseStatus || ''] || 'secondary'}`}>
                  {promiseStatusLabel(row.promiseStatus)}
                </span>
                {row.promisePaidAmount != null && row.promiseStatus !== 'KEPT' && (
                  <span className="text-muted">paid {formatCurrency(row.promisePaidAmount)}</span>
                )}
              </div>
            </div>
          )}
        </div>
      );
    },
    width: '210px',
  },
  {
    name: 'Note',
    selector: (row) => row.note || '',
    cell: (row) => (
      <div className="py-2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <span className={`badge bg-${getContextBadgeColor(row.context || '')} text-white me-2`}>
          {row.context?.replace(/_/g, ' ')}
        </span>
        {row.note}
      </div>
    ),
    grow: 2,
    wrap: true,
  },
];

const CollectionNotes: React.FC<CollectionNotesProps> = ({ accountId, className = '', onNoteAdded }) => {
  const [newNote, setNewNote] = useState('');
  const [selectedContext, setSelectedContext] = useState<string>('');
  const [contactMethod, setContactMethod] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [outcome, setOutcome] = useState('');
  const [promiseAmount, setPromiseAmount] = useState('');
  const [promiseDate, setPromiseDate] = useState('');
  const [filterContext, setFilterContext] = useState<string | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [notes, setNotes] = useState<CollectionNote[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const user = useSelector((state: RootState) => state.auth.user);

  const fetchNotes = useCallback(async () => {
    if (!accountId) return;
    setIsLoading(true);
    setIsError(false);
    try {
      const response: CollectionNotePage = await getCollectionNotes(accountId, {
        context: filterContext || undefined,
        page,
        size: pageSize,
      });
      setNotes(Array.isArray(response?.content) ? response.content : []);
      setTotalRows(typeof response?.totalElements === 'number' ? response.totalElements : 0);
    } catch (err) {
      console.error('Failed to fetch collection notes:', err);
      setIsError(true);
      setNotes([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [accountId, filterContext, page, pageSize]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newNote.trim()) {
      toast.error('Note cannot be empty');
      return;
    }
    if (isPromise) {
      if (!(Number(promiseAmount) > 0)) {
        toast.error('Enter the amount the client promised to pay');
        return;
      }
      if (!promiseDate || promiseDate < today) {
        toast.error('Enter the date the client will pay by (today or later)');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await addCollectionNote(
        accountId,
        newNote.trim(),
        selectedContext || undefined,
        user?.name || user?.username || undefined,
        undefined,
        {
          contactMethod: contactMethod || undefined,
          contactPerson: contactPerson.trim() || undefined,
          outcome: outcome || undefined,
          promiseAmount: isPromise ? Number(promiseAmount) : undefined,
          promiseDate: isPromise ? promiseDate : undefined,
        },
      );
      setNewNote('');
      setContactMethod('');
      setContactPerson('');
      setOutcome('');
      setPromiseAmount('');
      setPromiseDate('');
      toast.success(isPromise ? 'Promise to pay recorded' : outcome ? 'Contact logged' : 'Note added successfully');
      setPage(0);
      fetchNotes();
      onNoteAdded?.();
    } catch (error: any) {
      console.error('Failed to add note:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPromise = outcome === 'PROMISE_TO_PAY';
  const today = toLocalDateString();

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPageSize(newPerPage);
    setPage(0);
  };

  return (
    <div className={`collection-notes ${className}`}>
      {/* Add Note Form */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <h6 className="fw-bold mb-1">
            <i className="fa fa-plus-circle me-2 text-success"></i>
            Log a contact or add a note
          </h6>
          <p className="text-muted small mb-3">
            Record every call, message or visit with the client and what came of it, so the next person can pick up from here.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label fw-semibold mb-2">Contact method</label>
                <select
                  className="form-select"
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Not a contact (note only)</option>
                  {Object.entries(CONTACT_METHOD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold mb-2">Spoke to</label>
                <input
                  type="text"
                  className="form-control"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  placeholder="Name and role"
                  maxLength={150}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold mb-2">Outcome</label>
                <select
                  className="form-select"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">None</option>
                  {Object.entries(CONTACT_OUTCOME_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold mb-2">Context</label>
                <select
                  className="form-select"
                  value={selectedContext}
                  onChange={(e) => setSelectedContext(e.target.value as string)}
                  disabled={isSubmitting}
                >
                  <option value="">Automatic</option>
                  {CONTEXT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {isPromise && (
              <div className="row g-3 mb-3 p-3 mx-0 rounded" style={{ backgroundColor: '#eef4ff' }}>
                <div className="col-md-4">
                  <label className="form-label fw-semibold mb-2">
                    Amount promised <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">R</span>
                    <input
                      type="number"
                      className="form-control"
                      min="0.01"
                      step="0.01"
                      value={promiseAmount}
                      onChange={(e) => setPromiseAmount(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold mb-2">
                    Will pay by <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    min={today}
                    value={promiseDate}
                    onChange={(e) => setPromiseDate(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <small className="text-muted">
                    Marked kept once payments from today to this date cover the amount, broken if the date passes without.
                    A new promise replaces the one still pending.
                  </small>
                </div>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2">Note</label>
              <textarea
                className="form-control"
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={outcome ? 'What was said or agreed' : 'Enter your collection note here...'}
                disabled={isSubmitting}
                maxLength={2000}
              />
              <small className="text-muted">{newNote.length}/2000 characters</small>
            </div>
            <button
              type="submit"
              className="btn btn-success btn-sm"
              disabled={isSubmitting || !newNote.trim()}
            >
              {isSubmitting ? (
                <React.Fragment>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Adding...
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <i className="fa fa-plus me-2"></i>
                  {isPromise ? 'Record promise to pay' : outcome || contactMethod ? 'Log contact' : 'Add Note'}
                </React.Fragment>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Notes Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0 py-3 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 fw-bold">
            <i className="fa fa-sticky-note me-2 text-success"></i>
            Follow-up history
          </h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small me-1">Filter:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={filterContext}
              onChange={(e) => {
                setFilterContext(e.target.value as string | '');
                setPage(0);
              }}
            >
              <option value="">All Contexts</option>
              {CONTEXT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading notes...</span>
              </div>
              <p className="mt-2 text-muted mb-0">Loading notes...</p>
            </div>
          ) : isError ? (
            <div className="text-center py-4">
              <i className="fa fa-exclamation-triangle fa-2x text-warning mb-2 d-block"></i>
              <p className="text-muted mb-0">
                Unable to load collection notes. The notes service may be temporarily unavailable.
              </p>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-4">
              <i className="fa fa-sticky-note fa-2x text-muted mb-2 d-block"></i>
              <p className="text-muted mb-0">
                {filterContext
                  ? `No notes found for context: ${filterContext.replace(/_/g, ' ')}`
                  : 'No collection notes yet. Add the first note!'}
              </p>
            </div>
          ) : (
            <DataTable
              columns={collectionNoteColumns}
              data={notes}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationComponentOptions={{
                rowsPerPageText: 'Rows per page:',
                rangeSeparatorText: 'of',
              }}
              highlightOnHover
              striped
              responsive
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionNotes;